import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Award, Download, Mail, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Certificate panel — viewer-facing.
 * Locked until 24 Jun 2026 15:00 BST. After unlock: download PDF + email it.
 */
export default function CertificatePanel() {
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/certificate/status");
        setStatus(r.data);
      } catch {
        setStatus({ unlocked: false });
      }
    })();
  }, []);

  if (!status) return null;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/certificate/me", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DFTB26-certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate certificate");
    } finally {
      setDownloading(false);
    }
  };

  const emailPdf = async () => {
    setEmailing(true);
    try {
      const r = await api.post("/certificate/email");
      toast.success(`Sent to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't email certificate");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <section
      id="certificate"
      className="rounded-3xl overflow-hidden ring-1 ring-black/5"
      data-testid="certificate-panel"
    >
      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white p-6 sm:p-8 relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-12 w-12 rounded-2xl bg-[#28BBE3]/20 ring-1 ring-[#28BBE3]/30 flex items-center justify-center shrink-0">
              <Award className="text-[#28BBE3]" size={22} strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">Attendance certificate</div>
              <h3 className="mt-1 font-heading text-xl sm:text-2xl font-medium tracking-tight">
                {status.unlocked ? "Your certificate is ready" : "Coming soon"}
              </h3>
              <p className="mt-1.5 text-sm text-white/70 max-w-xl leading-relaxed">
                {status.unlocked ? (
                  <>Download your personalised certificate of attendance for <strong className="text-white">DFTB26 — Back to Basics</strong>, or have it emailed to you for safekeeping.</>
                ) : (
                  <>Your attendance certificate becomes available after the closing keynote on <strong className="text-white">Wednesday 24 June at 3pm BST</strong>. We&apos;ll unlock it here automatically.</>
                )}
              </p>
              {status.attendee_name && (
                <p className="mt-2 text-xs text-white/50">
                  Issued to: <span className="text-white/80 font-medium">{status.attendee_name}</span>
                </p>
              )}
            </div>
          </div>

          {status.unlocked ? (
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <Button
                onClick={downloadPdf}
                disabled={downloading}
                className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white px-5 shadow-[0_10px_30px_-12px_rgba(40,187,227,0.6)]"
                data-testid="certificate-download-btn"
              >
                <Download size={14} className="mr-2" />
                {downloading ? "Preparing…" : "Download PDF"}
              </Button>
              <Button
                onClick={emailPdf}
                disabled={emailing}
                variant="outline"
                className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
                data-testid="certificate-email-btn"
              >
                <Mail size={14} className="mr-2" />
                {emailing ? "Sending…" : "Email it to me"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 ring-1 ring-white/10 text-xs text-white/70 shrink-0">
              <Lock size={12} />
              Unlocks {fmtUnlock(status.unlock_at)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function fmtUnlock(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "24 June, 3pm BST";
  }
}
